import React, { useEffect, useRef, useState } from 'react';
import loadable from '@loadable/component';
import { useHistory } from 'react-router-dom';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { REVIEW_TYPE_OF_PROVIDER, REVIEW_TYPE_OF_CUSTOMER, propTypes } from '../../util/types';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  parse,
  PROFILE_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
  isNotFoundError,
} from '../../util/errors';
import {
  getDetailCustomFieldValue,
  getFieldValue,
  pickCustomFieldProps,
} from '../../util/fieldHelpers';
import { getSellerCategoryLabel } from '../../util/sellerCardData';
import {
  getCurrentUserTypeRoles,
  hasPermissionToViewData,
  isApprovedSeller,
  isSellerApplicationPending,
  isUserAuthorized,
} from '../../util/userHelpers';
import { richText } from '../../util/richText';
import { createResourceLocatorString } from '../../util/routes';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  Heading,
  H4,
  Page,
  AvatarLarge,
  NamedLink,
  ListingCard,
  Reviews,
  Button,
  ButtonTabNavHorizontal,
  LayoutSideNavigation,
  NamedRedirect,
  CustomExtendedDataSection,
  IconSocialMediaInstagram,
  IconSocialMediaTwitter,
  VerifiedCreatorCardSkeleton,
  ShareListingButton,
  IconVerified,
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';

import css from './ProfilePage.module.css';
import IconCollection from '../../components/IconCollection/IconCollection';
import SimilarSellersFallback from './SimilarSellersFallback';
import {
  DEFAULT_PROFILE_LISTING_SORT,
  PROFILE_HEADER_AVATAR_VARIANTS,
  PROFILE_LISTING_SORTS,
  getValidProfileListingSort,
  querySimilarSellers,
} from './ProfilePage.duck';

const MAX_MOBILE_SCREEN_WIDTH = 768;
const MIN_LENGTH_FOR_LONG_WORDS = 20;
const PROFILE_LISTINGS_INITIAL_COUNT = 8;
const PROFILE_HEADER_AVATAR_SIZES = '(max-width: 1200px) 146px, 292px';
const PROFILE_HEADER_USER_FIELD_KEYS = ['seller_category', 'instagram', 'x', 'tiktok'];
const HIDDEN_PROFILE_USER_FIELD_KEYS = ['onlyfans'];
const SIMILAR_SELLERS_SKELETON_COUNT = 4;
const PROFILE_LISTING_SORT_LABELS = {
  createdAt: 'ProfilePage.sortNewestFirst',
  '-createdAt': 'ProfilePage.sortOldestFirst',
  '-price': 'ProfilePage.sortPriceLowToHigh',
  price: 'ProfilePage.sortPriceHighToLow',
};
const PROFILE_LISTING_SORT_OPTIONS = PROFILE_LISTING_SORTS.map(key => ({
  key,
  labelId: PROFILE_LISTING_SORT_LABELS[key],
}));

// Swiper imports CSS that is not SSR-safe in the server bundle, so keep the
// carousel as a client-only enhancement and render a static fallback on SSR.
const SimilarSellersSlider = loadable(
  () => import(/* webpackChunkName: "SimilarSellersSlider" */ './SimilarSellersSlider'),
  { ssr: false }
);

const TikTokIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64v-3.5a6.67 6.67 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"
      fill="currentColor"
    />
  </svg>
);

const ensureExternalUrl = (value, platformBase = null) => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  // Bare username (no dot means no domain) — build the platform URL
  if (platformBase && !trimmed.includes('.')) {
    const cleanHandle = trimmed.replace(/^@/, '');
    return `${platformBase}${cleanHandle}`;
  }
  return `https://${trimmed}`;
};

const getSocialHandle = (value, platformBase = null) => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  if (!trimmed) {
    return null;
  }

  const normalizedUrl = ensureExternalUrl(trimmed, platformBase);
  try {
    const url = new URL(normalizedUrl);
    const username = url.pathname
      .split('/')
      .filter(Boolean)
      .find(pathPart => !['home', 'share', 'intent'].includes(pathPart.toLowerCase()));

    return username ? `@${username.replace(/^@/, '')}` : trimmed;
  } catch (e) {
    return trimmed.startsWith('@') ? trimmed : `@${trimmed}`;
  }
};

const createSimilarSellerCardData = (seller, userFieldConfig, routeConfiguration) => {
  const publicData = seller?.attributes?.profile?.publicData || {};
  const displayName = seller?.attributes?.profile?.displayName;
  const sellerId = seller?.id?.uuid;

  if (!sellerId || !displayName) {
    return null;
  }

  return {
    id: sellerId,
    category: getSellerCategoryLabel(publicData, userFieldConfig),
    name: displayName,
    subtitle: null,
    itemsCountText: null,
    profileImage: seller.profileImage,
    profileHref: createResourceLocatorString(
      'ProfilePage',
      routeConfiguration,
      { id: sellerId },
      {}
    ),
  };
};

export const AsideContent = props => {
  const {
    user,
    displayName,
    showLinkToProfileSettingsPage,
    bio,
    listingsCount = 0,
    publicData,
    intl,
    userFieldConfig,
    showVerifiedBadge,
  } = props;

  const sellerCategoryLabel = getSellerCategoryLabel(publicData, userFieldConfig);
  const rawJobTitle =
    sellerCategoryLabel || publicData?.jobTitle || publicData?.occupation || publicData?.subtitle;
  const jobTitle =
    typeof rawJobTitle === 'string' && rawJobTitle.trim()
      ? rawJobTitle
      : intl.formatMessage({ id: 'ProfilePage.profileTitleDefault' });

  const bioWithLinks = bio
    ? richText(bio, {
        linkify: true,
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
        longWordClass: css.longWord,
      })
    : null;

  const aboutBody = bioWithLinks || <FormattedMessage id="ProfilePage.defaultBio" />;

  const instagramValue =
    typeof publicData?.instagram === 'string' ? publicData.instagram.trim() : '';
  const xValue = typeof publicData?.x === 'string' ? publicData.x.trim() : '';
  const tiktokValue = typeof publicData?.tiktok === 'string' ? publicData.tiktok.trim() : '';
  const instagramHref = ensureExternalUrl(instagramValue, 'https://www.instagram.com/');
  const xHref = ensureExternalUrl(xValue, 'https://x.com/');
  const tiktokHref = ensureExternalUrl(tiktokValue, 'https://www.tiktok.com/@');
  const instagramHandle = getSocialHandle(instagramValue, 'https://www.instagram.com/');
  const xHandle = getSocialHandle(xValue, 'https://x.com/');
  const tiktokHandle = getSocialHandle(tiktokValue, 'https://www.tiktok.com/@');
  const showSocialLinks = instagramHref || xHref || tiktokHref;
  const showApplyToBeSeller =
    showLinkToProfileSettingsPage && !isApprovedSeller(user) && !isSellerApplicationPending(user);

  return (
    <div className={css.asideContent}>
      <div className={css.profileHeaderCard}>
        <div className={css.profileHeaderBanner} aria-hidden />
        <div className={css.profileHeaderBody}>
          <div className={css.profileHeaderLayout}>
            <div className={css.profileHeaderAvatarColumn}>
              <div className={css.profileHeaderAvatarWrap}>
                <AvatarLarge
                  className={css.profileHeaderAvatarShape}
                  user={user}
                  imageVariants={PROFILE_HEADER_AVATAR_VARIANTS}
                  renderSizes={PROFILE_HEADER_AVATAR_SIZES}
                  disableProfileLink
                />
                {showVerifiedBadge ? (
                  <span
                    className={css.profileHeaderVerified}
                    aria-label={intl.formatMessage({ id: 'ProfilePage.verifiedBadge' })}
                  >
                    <span className={css.profileHeaderVerifiedIcon} aria-hidden="true">
                      <IconVerified />
                    </span>
                  </span>
                ) : null}
              </div>
            </div>

            <div className={css.profileHeaderMain}>
              <div className={css.profileHeaderTopRow}>
                <div className={css.profileHeaderIdentity}>
                  {displayName ? <h1 className={css.profileHeaderName}>{displayName}</h1> : null}
                  <p className={css.profileHeaderRoleBadge}>{jobTitle}</p>
                </div>
                <div className={css.profileHeaderItemsStat}>
                  <ShareListingButton className={css.profileHeaderShareButton} />
                  <div className={css.profileHeaderItemsDivider} aria-hidden />
                  <div className={css.profileHeaderItemsBlock}>
                    <span className={css.profileHeaderItemsCount}>{listingsCount}</span>
                    <span className={css.profileHeaderItemsLabel}>
                      <FormattedMessage id="ProfilePage.itemsLabel" />
                    </span>
                  </div>
                </div>
              </div>

              {showLinkToProfileSettingsPage ? (
                <div className={css.profileHeaderEditLinks}>
                  <NamedLink className={css.profileHeaderEditLink} name="ProfileSettingsPage">
                    <FormattedMessage id="ProfilePage.editProfileLinkMobile" />
                  </NamedLink>
                  <NamedLink
                    className={css.profileHeaderEditLinkDesktop}
                    name="ProfileSettingsPage"
                  >
                    <FormattedMessage id="ProfilePage.editProfileLinkDesktop" />
                  </NamedLink>
                  {showApplyToBeSeller ? (
                    <NamedLink
                      className={css.profileHeaderApplySellerLink}
                      name="ProfileSettingsPage"
                      to={{ search: 'applySeller=true' }}
                    >
                      <FormattedMessage id="ProfilePage.applyToBeSeller" />
                    </NamedLink>
                  ) : null}
                </div>
              ) : null}

              <section className={css.profileHeaderAbout} aria-labelledby="profile-about-heading">
                <h2 id="profile-about-heading" className={css.profileHeaderAboutLabel}>
                  <FormattedMessage id="ProfilePage.aboutHeading" />
                </h2>
                <div className={css.profileHeaderAboutText}>{aboutBody}</div>
              </section>

              {showSocialLinks ? (
                <div className={css.profileHeaderSocial} role="list">
                  {instagramHref ? (
                    <a
                      className={css.profileHeaderSocialCard}
                      href={instagramHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      role="listitem"
                    >
                      <span className={css.profileHeaderSocialIcon}>
                        <IconCollection name="instagram" />
                      </span>
                      <span className={css.profileHeaderSocialText}>
                        <span className={css.profileHeaderSocialPlatform}>
                          <FormattedMessage id="ProfilePage.socialInstagram" />
                        </span>
                        <span className={css.profileHeaderSocialHandle}>{instagramHandle}</span>
                      </span>
                    </a>
                  ) : null}
                  {xHref ? (
                    <a
                      className={css.profileHeaderSocialCard}
                      href={xHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      role="listitem"
                    >
                      <span className={css.profileHeaderSocialIcon}>
                        <IconCollection name="twitter" />
                      </span>
                      <span className={css.profileHeaderSocialText}>
                        <span className={css.profileHeaderSocialPlatform}>
                          <FormattedMessage id="ProfilePage.socialTwitter" />
                        </span>
                        <span className={css.profileHeaderSocialHandle}>{xHandle}</span>
                      </span>
                    </a>
                  ) : null}
                  {tiktokHref ? (
                    <a
                      className={css.profileHeaderSocialCard}
                      href={tiktokHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      role="listitem"
                    >
                      <span className={css.profileHeaderSocialIconTiktok}>
                        <TikTokIcon />
                      </span>
                      <span className={css.profileHeaderSocialText}>
                        <span className={css.profileHeaderSocialPlatform}>
                          <FormattedMessage id="ProfilePage.socialTiktok" />
                        </span>
                        <span className={css.profileHeaderSocialHandle}>{tiktokHandle}</span>
                      </span>
                    </a>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ReviewsErrorMaybe = props => {
  const { queryReviewsError } = props;
  return queryReviewsError ? (
    <p className={css.error}>
      <FormattedMessage id="ProfilePage.loadingReviewsFailed" />
    </p>
  ) : null;
};

export const MobileReviews = props => {
  const { reviews, queryReviewsError } = props;
  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);
  return (
    <div className={css.mobileReviews}>
      <H4 as="h2" className={css.mobileReviewsTitle}>
        <FormattedMessage
          id="ProfilePage.reviewsFromMyCustomersTitle"
          values={{ count: reviewsOfProvider.length }}
        />
      </H4>
      <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
      <Reviews reviews={reviewsOfProvider} />
      <H4 as="h2" className={css.mobileReviewsTitle}>
        <FormattedMessage
          id="ProfilePage.reviewsAsACustomerTitle"
          values={{ count: reviewsOfCustomer.length }}
        />
      </H4>
      <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
      <Reviews reviews={reviewsOfCustomer} />
    </div>
  );
};

export const DesktopReviews = props => {
  const { reviews, queryReviewsError, userTypeRoles, intl } = props;
  const { customer: isCustomerUserType, provider: isProviderUserType } = userTypeRoles;

  const initialReviewState = !isProviderUserType
    ? REVIEW_TYPE_OF_CUSTOMER
    : REVIEW_TYPE_OF_PROVIDER;
  const [showReviewsType, setShowReviewsType] = useState(initialReviewState);

  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);
  const isReviewTypeProviderSelected = showReviewsType === REVIEW_TYPE_OF_PROVIDER;
  const isReviewTypeCustomerSelected = showReviewsType === REVIEW_TYPE_OF_CUSTOMER;
  const providerReviewsMaybe = isProviderUserType
    ? [
        {
          text: (
            <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
              <FormattedMessage
                id="ProfilePage.reviewsFromMyCustomersTitle"
                values={{ count: reviewsOfProvider.length }}
              />
            </Heading>
          ),
          selected: isReviewTypeProviderSelected,
          onClick: () => setShowReviewsType(REVIEW_TYPE_OF_PROVIDER),
        },
      ]
    : [];

  const customerReviewsMaybe = isCustomerUserType
    ? [
        {
          text: (
            <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
              <FormattedMessage
                id="ProfilePage.reviewsAsACustomerTitle"
                values={{ count: reviewsOfCustomer.length }}
              />
            </Heading>
          ),
          selected: isReviewTypeCustomerSelected,
          onClick: () => setShowReviewsType(REVIEW_TYPE_OF_CUSTOMER),
        },
      ]
    : [];
  const desktopReviewTabs = [...providerReviewsMaybe, ...customerReviewsMaybe];

  return (
    <div className={css.desktopReviews}>
      <div className={css.desktopReviewsWrapper}>
        <ButtonTabNavHorizontal
          className={css.desktopReviewsTabNav}
          tabs={desktopReviewTabs}
          ariaLabel={intl.formatMessage({ id: 'ProfilePage.screenreader.reviewsNav' })}
        />

        <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />

        {isReviewTypeProviderSelected ? (
          <Reviews reviews={reviewsOfProvider} />
        ) : (
          <Reviews reviews={reviewsOfCustomer} />
        )}
      </div>
    </div>
  );
};

export const CustomUserFields = props => {
  const { publicData, metadata, userFieldConfig, intl } = props;

  const shouldPickUserField = fieldConfig =>
    ['public', 'metadata'].includes(fieldConfig?.scope) &&
    !PROFILE_HEADER_USER_FIELD_KEYS.includes(fieldConfig?.key) &&
    !HIDDEN_PROFILE_USER_FIELD_KEYS.includes(fieldConfig?.key) &&
    fieldConfig?.showConfig?.displayInProfile !== false;
  const propsForCustomFields =
    pickCustomFieldProps(
      { publicData, metadata },
      userFieldConfig,
      'userType',
      shouldPickUserField
    ) || [];

  const pickUserFields = (filteredConfigs, config) => {
    const { key, schemaType, enumOptions, userTypeConfig = {}, showConfig = {} } = config;
    const isProfileHeaderField =
      PROFILE_HEADER_USER_FIELD_KEYS.includes(key) || HIDDEN_PROFILE_USER_FIELD_KEYS.includes(key);
    const { limitToUserTypeIds, userTypeIds } = userTypeConfig;
    const userType = publicData.userType;
    const isTargetUserType = !limitToUserTypeIds || userTypeIds.includes(userType);

    const { label, displayInProfile } = showConfig;
    const publicDataValue = getFieldValue(publicData, key);
    const metadataValue = getFieldValue(metadata, key);
    const value = publicDataValue !== null ? publicDataValue : metadataValue;

    if (!isProfileHeaderField && displayInProfile && isTargetUserType && value !== null) {
      const detailValue = getDetailCustomFieldValue(
        enumOptions,
        value,
        schemaType,
        key,
        label,
        intl,
        'ProfilePage'
      );

      return detailValue ? filteredConfigs.concat(detailValue) : filteredConfigs;
    }
    return filteredConfigs;
  };
  const sectionDetailsProps = {
    ...props,
    fieldConfigs: userFieldConfig,
    heading: 'ProfilePage.detailsTitle',
    rootClassName: css.userFieldSection,
  };

  return (
    <CustomExtendedDataSection
      sectionDetailsProps={sectionDetailsProps}
      propsForCustomFields={propsForCustomFields}
      idPrefix="profilePage"
      pickExtendedDataFields={pickUserFields}
      rootClassName={css.userFieldSection}
    />
  );
};

export const MainContent = props => {
  const [mounted, setMounted] = useState(false);
  const [showAllListings, setShowAllListings] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    userShowError,
    bio,
    displayName,
    listings = [],
    queryListingsError,
    reviews = [],
    queryReviewsError,
    publicData,
    metadata,
    userFieldConfig,
    intl,
    hideReviews,
    userTypeRoles,
    listingsSort = DEFAULT_PROFILE_LISTING_SORT,
    onListingsSortChange,
  } = props;

  useEffect(() => {
    setShowAllListings(false);
  }, [listings.length, listingsSort]);

  const hasListings = listings.length > 0;
  const displayedListings =
    !hasListings || showAllListings || listings.length <= PROFILE_LISTINGS_INITIAL_COUNT
      ? listings
      : listings.slice(0, PROFILE_LISTINGS_INITIAL_COUNT);
  const showLoadMoreListings =
    hasListings && listings.length > PROFILE_LISTINGS_INITIAL_COUNT && !showAllListings;
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout =
    mounted && hasMatchMedia
      ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
      : true;

  const hasBio = !!bio;

  const listingsContainerClasses = classNames(css.listingsContainer, {
    [css.withBioMissingAbove]: !hasBio,
  });

  if (userShowError || queryListingsError) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProfilePage.loadingDataFailed" />
      </p>
    );
  }
  return (
    <div>
      {displayName ? (
        <CustomUserFields
          publicData={publicData}
          metadata={metadata}
          userFieldConfig={userFieldConfig}
          intl={intl}
        />
      ) : null}

      {hasListings ? (
        <div className={listingsContainerClasses}>
          <div className={css.listingsToolbar}>
            <div className={css.listingsToolbarLeft}>
              <h2 className={css.listingsTab}>
                <FormattedMessage id="ProfilePage.listingsTab" />
              </h2>
            </div>
            <div className={css.listingsToolbarRight}>
              <span className={css.listingsSortLabel}>
                <FormattedMessage id="ProfilePage.sortByLabel" />
              </span>
              <select
                className={css.listingsSortSelect}
                value={listingsSort}
                onChange={e => onListingsSortChange(e.target.value)}
                aria-label={intl.formatMessage({ id: 'ProfilePage.sortListingsAria' })}
              >
                {PROFILE_LISTING_SORT_OPTIONS.map(option => (
                  <option key={option.key} value={option.key}>
                    {intl.formatMessage({ id: option.labelId })}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <ul className={css.listings}>
            {displayedListings.map(l => (
              <li className={css.listing} key={l.id.uuid}>
                <ListingCard
                  listing={l}
                  variant="profileProductGrid"
                  showAuthorInfo={false}
                  intl={intl}
                />
              </li>
            ))}
          </ul>
          {showLoadMoreListings ? (
            <div className={css.listingsLoadMore}>
              <Button
                type="button"
                className={css.listingsLoadMoreButton}
                onClick={() => setShowAllListings(true)}
              >
                <FormattedMessage id="ProfilePage.loadMoreListings" />
                <IconCollection name="arrowRight" />
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
      {/* {hideReviews ? null : isMobileLayout ? (
        <MobileReviews
          reviews={reviews}
          queryReviewsError={queryReviewsError}
          userTypeRoles={userTypeRoles}
        />
      ) : (
        <DesktopReviews
          reviews={reviews}
          queryReviewsError={queryReviewsError}
          userTypeRoles={userTypeRoles}
          intl={intl}
        />
      )} */}
    </div>
  );
};

/**
 * ProfilePageComponent
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.scrollingDisabled - Whether the scrolling is disabled
 * @param {propTypes.currentUser} props.currentUser - The current user
 * @param {boolean} props.useCurrentUser - Whether to use the current user
 * @param {propTypes.user|propTypes.currentUser} props.user - The user
 * @param {propTypes.error} props.userShowError - The user show error
 * @param {propTypes.error} props.queryListingsError - The query listings error
 * @param {Array<propTypes.listing|propTypes.ownListing>} props.listings - The listings
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.queryReviewsError - The query reviews error
 * @returns {JSX.Element} ProfilePageComponent
 */
export const ProfilePageComponent = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const similarSellersSectionRef = useRef(null);
  const similarSellersUserScrolledRef = useRef(false);
  const [mounted, setMounted] = useState(false);
  const [similarSellersRequested, setSimilarSellersRequested] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    scrollingDisabled,
    params: pathParams,
    currentUser,
    useCurrentUser,
    dispatch,
    userShowError,
    user,
    listings = [],
    similarSellers = [],
    querySimilarSellersInProgress,
    ...rest
  } = props;
  const isVariant = pathParams.variant?.length > 0;
  const isPreview = isVariant && pathParams.variant === PROFILE_PAGE_PENDING_APPROVAL_VARIANT;

  // Stripe's onboarding needs a business URL for each seller, but the profile page can be
  // too empty for the provider at the time they are creating their first listing.
  // To remedy the situation, we redirect Stripe's crawler to the landing page of the marketplace.
  // TODO: When there's more content on the profile page, we should consider by-passing this redirection.
  const searchParams = rest?.location?.search;
  const isStorefront = searchParams
    ? new URLSearchParams(searchParams)?.get('mode') === 'storefront'
    : false;
  if (isStorefront) {
    return <NamedRedirect name="LandingPage" />;
  }

  const isCurrentUser = currentUser?.id && currentUser?.id?.uuid === pathParams.id;
  const profileUser = useCurrentUser ? currentUser : user;
  const showVerifiedBadge = isApprovedSeller(profileUser);
  const { bio, displayName, publicData, metadata } = profileUser?.attributes?.profile || {};
  const { userFields } = config.user;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const isUnauthorizedOnPrivateMarketplace = isPrivateMarketplace && isUnauthorizedUser;
  const hasUserPendingApprovalError = isErrorUserPendingApproval(userShowError);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasNoViewingRightsOnPrivateMarketplace = isPrivateMarketplace && hasNoViewingRightsUser;
  const sellerCategory =
    typeof publicData?.seller_category === 'string' ? publicData.seller_category.trim() : '';
  const currentSearchParams = parse(searchParams);
  const listingsSort = getValidProfileListingSort(currentSearchParams?.sort);
  const handleListingsSortChange = sort => {
    const selectedSort = getValidProfileListingSort(sort);

    history.push(
      createResourceLocatorString(
        'ProfilePage',
        routeConfiguration,
        pathParams,
        { ...currentSearchParams, sort: selectedSort },
        rest.location.hash || ''
      )
    );
  };

  const userTypeRoles = getCurrentUserTypeRoles(config, profileUser);
  const similarSellerCards = similarSellers
    .map(seller => createSimilarSellerCardData(seller, userFields, routeConfiguration))
    .filter(seller => seller != null);
  const canLazyLoadSimilarSellers =
    !isPreview && !!profileUser?.id && !!sellerCategory && !hasNoViewingRightsOnPrivateMarketplace;
  const showSimilarSellersSection = similarSellerCards.length > 0 || canLazyLoadSimilarSellers;

  const isDataLoaded = isPreview
    ? currentUser != null || userShowError != null
    : hasNoViewingRightsOnPrivateMarketplace
    ? currentUser != null || userShowError != null
    : user != null || userShowError != null;

  useEffect(() => {
    setSimilarSellersRequested(false);
  }, [pathParams.id, sellerCategory]);

  useEffect(() => {
    if (!mounted || !canLazyLoadSimilarSellers || similarSellersRequested) {
      return () => {};
    }

    const target = similarSellersSectionRef.current;
    if (!target) {
      return () => {};
    }

    const loadSimilarSellers = () => {
      setSimilarSellersRequested(true);
      dispatch(querySimilarSellers(profileUser.id, config));
    };
    const isTargetInViewport = () => {
      const rect = target.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      return rect.top < viewportHeight && rect.bottom > 0;
    };

    if (typeof window === 'undefined' || typeof window.IntersectionObserver === 'undefined') {
      const handleScroll = () => {
        similarSellersUserScrolledRef.current = true;
        if (isTargetInViewport()) {
          window.removeEventListener('scroll', handleScroll);
          loadSimilarSellers();
        }
      };

      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    }

    const handleScroll = () => {
      similarSellersUserScrolledRef.current = true;
      if (isTargetInViewport()) {
        observer.disconnect();
        window.removeEventListener('scroll', handleScroll);
        loadSimilarSellers();
      }
    };

    const observer = new window.IntersectionObserver(entries => {
      if (similarSellersUserScrolledRef.current && entries.some(entry => entry.isIntersecting)) {
        observer.disconnect();
        window.removeEventListener('scroll', handleScroll);
        loadSimilarSellers();
      }
    });

    window.addEventListener('scroll', handleScroll, { passive: true });
    observer.observe(target);
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, [
    canLazyLoadSimilarSellers,
    config,
    dispatch,
    mounted,
    profileUser?.id,
    sellerCategory,
    similarSellersRequested,
  ]);

  const schemaTitleVars = { name: displayName, marketplaceName: config.marketplaceName };
  const schemaTitle = intl.formatMessage({ id: 'ProfilePage.schemaTitle' }, schemaTitleVars);

  if (!isDataLoaded) {
    return null;
  } else if (!isPreview && isNotFoundError(userShowError)) {
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (!isPreview && (isUnauthorizedOnPrivateMarketplace || hasUserPendingApprovalError)) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (!isPreview && hasNoViewingRightsOnPrivateMarketplace && !isCurrentUser) ||
    isErrorNoViewingPermission(userShowError)
  ) {
    // Someone without viewing rights on a private marketplace is trying to
    // view a profile page that is not their own – redirect to NoAccessPage
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  } else if (!isPreview && isForbiddenError(userShowError)) {
    // This can happen if private marketplace mode is active, but it's not reflected through asset yet.
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  } else if (isPreview && mounted && !isCurrentUser) {
    // Someone is manipulating the URL, redirect to current user's profile page.
    return isCurrentUser === false ? (
      <NamedRedirect name="ProfilePage" params={{ id: currentUser?.id?.uuid }} />
    ) : null;
  } else if ((isPreview || isPrivateMarketplace) && !mounted) {
    // This preview of the profile page is not rendered on server-side
    // and the first pass on client-side should render the same UI.
    return null;
  }

  // This is rendering normal profile page (not preview for pending-approval)
  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ProfilePage',
        mainEntity: {
          '@type': 'Person',
          name: profileUser?.attributes?.profile?.displayName,
        },
        name: schemaTitle,
      }}
    >
      <TopbarContainer />
      <div className={css.profilePageContent}>
        <AsideContent
          user={profileUser}
          showLinkToProfileSettingsPage={mounted && isCurrentUser}
          displayName={displayName}
          bio={bio}
          listingsCount={listings.length}
          publicData={publicData}
          intl={intl}
          userFieldConfig={userFields}
          showVerifiedBadge={showVerifiedBadge}
        />
        <MainContent
          bio={bio}
          displayName={displayName}
          userShowError={userShowError}
          publicData={publicData}
          metadata={metadata}
          userFieldConfig={userFields}
          hideReviews={hasNoViewingRightsOnPrivateMarketplace}
          intl={intl}
          userTypeRoles={userTypeRoles}
          listings={listings}
          listingsSort={listingsSort}
          onListingsSortChange={handleListingsSortChange}
          {...rest}
        />
        {showSimilarSellersSection ? (
          <section className={css.similarSellers} aria-labelledby="profile-similar-sellers-heading">
            <div className={css.similarSellersInner}>
              <header ref={similarSellersSectionRef} className={css.similarSellersHeader}>
                <Heading
                  as="h2"
                  styledAs="h2"
                  id="profile-similar-sellers-heading"
                  rootClassName={css.similarSellersTitle}
                >
                  <FormattedMessage id="ProfilePage.similarSellersTitle" />
                </Heading>
                <p className={css.similarSellersSubtitle}>
                  <FormattedMessage id="ProfilePage.similarSellersSubtitle" />
                </p>
              </header>
              {similarSellerCards.length > 0 ? (
                <SimilarSellersSlider
                  sellers={similarSellerCards}
                  intl={intl}
                  fallback={<SimilarSellersFallback sellers={similarSellerCards} intl={intl} />}
                />
              ) : querySimilarSellersInProgress || !similarSellersRequested ? (
                <div className={css.similarSellersSkeletonGrid}>
                  {Array.from({ length: SIMILAR_SELLERS_SKELETON_COUNT }, (_, index) => (
                    <VerifiedCreatorCardSkeleton
                      className={css.similarSellersSkeletonItem}
                      key={`similar-seller-skeleton-${index}`}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          </section>
        ) : null}
      </div>
      <FooterContainer />
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    userId,
    userShowError,
    queryListingsError,
    userListingRefs,
    similarSellerRefs = [],
    querySimilarSellersInProgress,
    reviews = [],
    queryReviewsError,
  } = state.ProfilePage;
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;

  // Show currentUser's data if it's not approved yet
  const isCurrentUser = userId?.uuid === currentUser?.id?.uuid;
  const useCurrentUser =
    isCurrentUser && !(isUserAuthorized(currentUser) && hasPermissionToViewData(currentUser));

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    useCurrentUser,
    user,
    userShowError,
    queryListingsError,
    listings: getMarketplaceEntities(state, userListingRefs),
    similarSellers: getMarketplaceEntities(state, similarSellerRefs),
    querySimilarSellersInProgress,
    reviews,
    queryReviewsError,
  };
};

const ProfilePage = compose(connect(mapStateToProps))(ProfilePageComponent);

export default ProfilePage;
